import { useEffect } from 'react';
import type { CSSProperties } from 'react';
import { Button, Col, DatePicker, Form, Input, Row, Space, Typography } from 'antd';
import type { FormInstance } from 'antd';
import { CATEGORIA_ESTADO_SERVICIO, ESTADOS_INICIALES, transicionesPermitidas } from '@aid/shared';
import type {
  CatalogoResponse,
  CreateServicioDto,
  EstadoInicialKey,
  EstadoServicioKey,
  ServicioResponse,
} from '@aid/shared';
import { useT } from '../../../locales';
import { ClienteSelect } from '../../../components/ClienteSelect';
import { PlanSelect } from '../../../components/PlanSelect';
import { TipoServicioSelect } from '../../../components/TipoServicioSelect';
import { CatalogoSelect } from '../../../components/CatalogoSelect';
import { DepartamentoMunicipioSelect } from '../../../components/DepartamentoMunicipioSelect';
import { MapaUbicacionPicker } from '../../../components/MapaUbicacionPicker';
import type { UbicacionCoords } from '../../../components/MapaUbicacionPicker';
import type { Dayjs } from 'dayjs';
import { parseDateOnly, toDateOnlyString } from '../../../lib/date';

const itemStyle: CSSProperties = { marginBottom: 4 };

const { Text } = Typography;

interface ServicioFormProps {
  mode: 'create' | 'edit';
  initialValues?: ServicioResponse | null;
  onSubmit: (values: CreateServicioDto) => void;
  loading?: boolean;
  preselectedClienteId?: number;
  form?: FormInstance;
  hideSubmit?: boolean;
}

export function ServicioForm({
  mode,
  initialValues,
  onSubmit,
  loading,
  preselectedClienteId,
  form: externalForm,
  hideSubmit = false,
}: ServicioFormProps) {
  const t = useT();
  const [internalForm] = Form.useForm();
  const form = externalForm ?? internalForm;

  const latitud = Form.useWatch('latitud', form) as number | null | undefined;
  const longitud = Form.useWatch('longitud', form) as number | null | undefined;
  const ubicacion: UbicacionCoords | null =
    latitud != null && longitud != null ? { lat: latitud, lng: longitud } : null;

  // Aviso en vivo cuando el plan cambia en edición: el efecto real es el 1°
  // del próximo mes (regla del backend, servicio_plan_historial).
  const planSeleccionado = Form.useWatch('idPlan', form) as number | null | undefined;
  const planCambioPendiente =
    mode === 'edit' &&
    initialValues != null &&
    planSeleccionado != null &&
    planSeleccionado !== initialValues.idPlan;

  // No se usa `initialValues` del Form solo porque no cubre los dos modos:
  // (1) la data llega async (useServicio) y el store del form se crea antes
  //     del primer render con data → initialValues solo aplica al mount del
  //     store, no cuando la query resuelve;
  // (2) en create con `preselectedClienteId` no hay initialValues que mapear,
  //     hace falta setFieldValue programático.
  // El efecto corre en cada mount (los modales usan destroyOnHidden, que
  // remonta el formulario) y pisa valores stale del store persistente.
  // Set explícito del store del form (ver comentario del useEffect más arriba
  // sobre por qué no usamos initialValues del Form).
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        idCliente: initialValues.idCliente,
        idPlan: initialValues.idPlan,
        idTipoServicio: initialValues.idTipoServicio,
        idEstado: initialValues.idEstado,
        idMunicipio: initialValues.idMunicipio,
        idDepartamento: initialValues.idDepartamento,
        direccion: initialValues.direccion ?? '',
        referencia: initialValues.referencia ?? '',
        latitud: initialValues.latitud,
        longitud: initialValues.longitud,
        // Date-only contract: parse component-wise, never as a UTC instant.
        fechaInicio: parseDateOnly(initialValues.fechaInicio) ?? undefined,
      });
    } else if (preselectedClienteId) {
      form.setFieldValue('idCliente', preselectedClienteId);
    }
  }, [initialValues, form, preselectedClienteId]);

  // Frontera de tipado: antd entrega Record<string, unknown>; el form manda
  // exactamente los campos de CreateServicioDto (fechas ya normalizadas).
  const handleFinish = (values: Record<string, unknown>) => {
    const payload: CreateServicioDto = {
      ...values,
      fechaInicio: values.fechaInicio ? toDateOnlyString(values.fechaInicio as Dayjs) : undefined,
    } as CreateServicioDto;
    if (mode === 'edit') {
      // Cliente, tipo de servicio y fechaInicio son inmutables en edición: no
      // viajan en el payload (el contrato de update ya no los expone).
      delete (payload as Record<string, unknown>).idCliente;
      delete (payload as Record<string, unknown>).idTipoServicio;
      delete (payload as Record<string, unknown>).fechaInicio;
    }
    onSubmit(payload);
  };

  // El select de estado depende del modo:
  // - edición con key conocida: estado actual + destinos manuales legales;
  // - creación: solo los estados iniciales (ESTADOS_INICIALES) — un servicio
  //   nuevo no puede nacer en SUSPENDIDO/BAJA/BAJA_AL_CORTE;
  // - edición con key desconocida o nula (datos legacy): todos los estados,
  //   display-only — el backend es el gate real.
  const keyActual: EstadoServicioKey | null =
    mode === 'edit' && initialValues?.estado?.key
      ? (initialValues.estado.key as EstadoServicioKey)
      : null;
  const filterEstadoOptions =
    mode === 'create'
      ? (c: CatalogoResponse) => ESTADOS_INICIALES.includes(c.key as EstadoInicialKey)
      : keyActual
        ? (c: CatalogoResponse) =>
            c.key === keyActual ||
            transicionesPermitidas(keyActual, 'manual').includes(c.key as EstadoServicioKey)
        : undefined;

  const handleUbicacionChange = (v: UbicacionCoords | null) => {
    form.setFieldsValue({ latitud: v?.lat ?? null, longitud: v?.lng ?? null });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      style={{ maxWidth: 600, width: '100%' }}
    >
      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            style={itemStyle}
            name="idCliente"
            label={t('pages.servicios.cliente')}
            rules={[{ required: true, message: t('pages.servicios.clienteRequired') }]}
          >
            <ClienteSelect disabled={mode === 'edit' || Boolean(preselectedClienteId)} />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            style={itemStyle}
            name="idPlan"
            label={t('pages.servicios.plan')}
            rules={[{ required: true, message: t('pages.servicios.planRequired') }]}
          >
            <PlanSelect placeholder={t('pages.servicios.selectPlan')} />
          </Form.Item>
          {planCambioPendiente && (
            <Text
              type="warning"
              style={{
                display: 'block',
                fontSize: 12,
                lineHeight: 1.4,
                marginTop: -2,
                marginBottom: 8,
              }}
            >
              {t('pages.servicios.planCambioAviso')}
            </Text>
          )}
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            style={itemStyle}
            name="idTipoServicio"
            label={t('pages.servicios.tipoServicio')}
            rules={[{ required: true, message: t('pages.servicios.tipoServicioRequired') }]}
          >
            <TipoServicioSelect
              disabled={mode === 'edit'}
              placeholder={t('pages.servicios.selectTipoServicio')}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            style={itemStyle}
            name="idEstado"
            label={t('pages.servicios.status')}
            rules={[{ required: true, message: t('pages.servicios.estadoRequired') }]}
          >
            <CatalogoSelect
              category={CATEGORIA_ESTADO_SERVICIO}
              placeholder={t('pages.servicios.selectEstado')}
              filterOptions={filterEstadoOptions}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <DepartamentoMunicipioSelect
            departamentoRules={[
              { required: true, message: t('pages.servicios.departamentoRequired') },
            ]}
            municipioRules={[{ required: true, message: t('pages.servicios.municipioRequired') }]}
          />
        </Col>
        <Col span={24}>
          <Form.Item style={itemStyle} name="direccion" label={t('pages.servicios.direccion')}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item style={itemStyle} name="referencia" label={t('pages.servicios.referencia')}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={24}>
          {/*
            Registran latitud/longitud en el form: Form.useWatch solo notifica
            campos REGISTRADOS (con Form.Item), y sin esto el picker nunca vería
            los cambios de setFieldsValue. El picker queda fuera del Form.Item
            porque un Form.Item sin name pisaría su value/onChange.
          */}
          <Form.Item name="latitud" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="longitud" hidden>
            <Input />
          </Form.Item>
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 6 }}>
              <Text style={{ fontSize: 14 }}>{t('pages.servicios.ubicacion')}</Text>
            </div>
            <MapaUbicacionPicker value={ubicacion} onChange={handleUbicacionChange} />
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            style={itemStyle}
            name="fechaInicio"
            label={t('pages.servicios.fechaInicio')}
            rules={[{ required: true, message: t('pages.servicios.fechaInicioRequired') }]}
          >
            <DatePicker style={{ width: '100%' }} disabled={mode === 'edit'} />
          </Form.Item>
        </Col>
        {/* Sin campo fechaFin: la fecha de fin la escribe la máquina de estados
            (BAJA → hoy, reversión → null). ServicioDetailModal sigue mostrando
            el valor persistido. */}
        <Col span={24}>
          <Form.Item style={itemStyle}>
            {!hideSubmit && (
              <Space wrap size={[8, 8]}>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {mode === 'create' ? t('pages.servicios.create') : t('common.saveChanges')}
                </Button>
              </Space>
            )}
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
}
